/* eslint-disable */
import React, { useEffect, useRef, useState, useCallback } from "react";
import cx from "classnames";
import { client, fileDownloadClient } from "../../services/axiosClient";
import fileDownload from "js-file-download";
import { useImmer } from "use-immer";
import { useTranslation } from "react-i18next";
import { Slide as ToastSlide, toast } from "react-toastify";
import { ThreeDots } from "react-loader-spinner";

// @material-ui/core components
import { makeStyles } from "@material-ui/core/styles";
import Checkbox from "@material-ui/core/Checkbox";
import Radio from "@material-ui/core/Radio";
import RadioGroup from "@material-ui/core/RadioGroup";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Tooltip from "@material-ui/core/Tooltip";

// @material-ui/icons
import {
  GetApp,
  DateRange,
  ViewColumn,
  Functions,
  CloudDownload,
  RadioButtonUnchecked,
  CheckCircle,
  HelpOutline,
  ExpandMore,
  ExpandLess,
} from "@material-ui/icons";

// core components
import Card from "../../components/Card/Card.js";
import CardBody from "../../components/Card/CardBody.js";
import Button from "../../components/CustomButtons/Button.js";
import FilterTag from "../../components/FilterTag/FilterTag";
import FilterDialog from "../../components/FilterDialog/FilterDialog";

import useThemeColors from "../../hooks/useThemeColors";
import { grayColor } from "../../assets/jss/material-dashboard-pro-react";
import dataExplorerStyleFn from "../../assets/jss/material-dashboard-pro-react/views/dataExplorerStyle";

export default function DataExplorer() {
  const { clientColor } = useThemeColors();
  const useStyles = makeStyles(dataExplorerStyleFn(clientColor));
  const classes = useStyles();
  const { t, i18n } = useTranslation();

  // --- Meta refs ---
  const filtersList = useRef([]);
  const filtersNames = useRef({});
  const filtersItems = useRef({});
  const filtersGroups = useRef({});
  const pnlLines = useRef([]);
  const dimensionGroupsRef = useRef([]);
  const volumeUnit = useRef("");
  const volumeUnitAlternatives = useRef([]);
  const limitsRef = useRef({
    max_download_rows: 200000,
  });

  // --- Cascading ---
  const cascadedItems = useRef({});
  const [isCascadeLoading, setIsCascadeLoading] = useState(false);
  const cascadePendingCount = useRef(0);

  // --- Loading state ---
  const [metaLoaded, setMetaLoaded] = useState(false);

  // --- User selections ---
  const [selectedDimensions, setSelectedDimensions] = useState([]);
  const [selectedValues, setSelectedValues] = useState([]);
  const [timeGranularity, setTimeGranularity] = useState("monthly");
  const [expandedParents, setExpandedParents] = useState([]);

  // --- Filters ---
  const [filters, setFilters] = useImmer({});
  const [filtersMode, setFiltersMode] = useImmer({});
  const [filtersSequence, setFiltersSequence] = useState(["period"]);
  const [filtersIndexes, setFiltersIndexes] = useImmer({});

  // --- FilterDialog state ---
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [filterModalCurrentFilter, setFilterModalCurrentFilter] = useState("");
  const [filterModalItemsChoices, setFilterModalItemsChoices] = useState([]);
  const [filterModalItemsSelected, setFilterModalItemsSelected] = useState([]);
  const [filterModalGroupChoices, setFilterModalGroupChoices] = useState([]);
  const [filterModalGroupSelected, setFilterModalGroupSelected] = useState("");
  const [filterModalFilterMode, setFilterModalFilterMode] = useState("");

  // --- Mandatory anchors ---
  const mandatoryAnchors = useRef([]);

  // --- Ref range filters (map: dimension → {mode, min, max}) ---
  const eligibleColorLines = useRef([]);
  const serveMarginAnchor = useRef("");
  const [selectedColorLine, setSelectedColorLine] = useState("");
  const [refRangeFilters, setRefRangeFilters] = useImmer({});
  const [filterModalRangeMode, setFilterModalRangeMode] = useState(null);
  const [filterModalRangeMin, setFilterModalRangeMin] = useState("");
  const [filterModalRangeMax, setFilterModalRangeMax] = useState("");

  const hasActiveRefRange = Object.keys(refRangeFilters).length > 0;
  const buildRefRangeFiltersArray = () => {
    const colorLine = selectedColorLine || serveMarginAnchor.current;
    return Object.entries(refRangeFilters).map(([dim, f]) => ({
      dimension: dim,
      mode: f.mode,
      min: f.min,
      max: f.max,
      color_line: colorLine,
    }));
  };

  // --- Estimate & Download ---
  const [estimate, setEstimate] = useState(null);
  const [isEstimating, setIsEstimating] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const notify = (text, type = "info", time = 3000) =>
    toast(t(text), {
      position: "top-right",
      autoClose: time,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: false,
      theme: "light",
      type: type,
      transition: ToastSlide,
    });

  // ======================================================================
  // Load metadata
  // ======================================================================
  useEffect(() => {
    client
      .post("api/data_explorer_meta/")
      .then((response) => {
        const data = response.data;
        const f = data.filters;
        filtersList.current = f.list;
        filtersNames.current = f.names;
        filtersItems.current = f.items;
        filtersGroups.current = f.groups;
        pnlLines.current = data.pnl_lines;
        dimensionGroupsRef.current = data.dimension_groups || [];
        volumeUnit.current = data.volume_unit;
        volumeUnitAlternatives.current = data.volume_unit_alternatives || [];
        limitsRef.current = data.limits;
        eligibleColorLines.current = data.eligible_color_lines || [];
        serveMarginAnchor.current = data.serve_margin_anchor || "";
        setSelectedColorLine(data.serve_margin_anchor || "");

        // Build mandatory anchors (always selected, not removable)
        const anchors = new Set();
        (data.revenue_reference_options || []).forEach((opt) => {
          if (opt.value !== "combined") anchors.add(opt.value);
        });
        if (data.net_revenue_anchor) anchors.add(data.net_revenue_anchor);
        if (data.serve_margin_anchor) anchors.add(data.serve_margin_anchor);
        mandatoryAnchors.current = [...anchors];

        // Default selection: "first level" = volume + parent lines (no parent)
        const firstLevelValues = [
          "volume",
          ...data.pnl_lines.filter((l) => !l.parent).map((l) => l.meta_name),
        ];
        setSelectedValues(firstLevelValues);

        let initFilters = {};
        let initMode = {};
        let initIndexes = {};
        Object.keys(f.names).forEach((name) => {
          initFilters[name] = name === "period" ? ["l12m"] : [];
          initMode[name] = name === "period" ? "group" : "";
          initIndexes[name] = name === "period" ? 0 : [];
        });
        setFilters(() => initFilters);
        setFiltersMode(() => initMode);
        setFiltersIndexes(() => initIndexes);
        setMetaLoaded(true);
      })
      .catch((error) => {
        console.error("Error loading data explorer meta:", error);
        setMetaLoaded(true);
      });
  }, []);

  // ======================================================================
  // Filter helpers
  // ======================================================================
  const getFilterItems = (raw_items, filter, mode) => {
    if (filter === "period" && mode === "items") {
      return raw_items.map(
        (month) =>
          t("months." + month[1].toString()) +
          "-" +
          month[0].toString().slice(-2),
      );
    }
    return raw_items;
  };

  const getChoicesList = (selected_filter, all_choices) => {
    if (selected_filter === "period") {
      return all_choices.map(
        (month) =>
          t("months." + month[1].toString()) +
          "-" +
          month[0].toString().slice(-2),
      );
    }
    return all_choices;
  };

  // ======================================================================
  // Dimension group helpers
  // ======================================================================
  const findGroupForDimension = useCallback((dim) => {
    for (const group of dimensionGroupsRef.current) {
      if (group.dimensions.includes(dim)) return group;
    }
    return null;
  }, []);

  const getChildDimsInGroup = useCallback(
    (dim) => {
      const group = findGroupForDimension(dim);
      if (!group) return [];
      const idx = group.dimensions.indexOf(dim);
      return group.dimensions.slice(idx + 1);
    },
    [findGroupForDimension],
  );

  // ======================================================================
  // Pre-fetch cascading items with loading indicator
  // ======================================================================
  const prefetchCascadedItems = useCallback(
    (parentDim, currentFilters, currentFiltersMode, currentFiltersSequence) => {
      const childDims = getChildDimsInGroup(parentDim);
      if (childDims.length === 0) return;

      const activeSequence = currentFiltersSequence.filter(
        (f) =>
          currentFilters[f] &&
          currentFilters[f].length > 0 &&
          currentFiltersMode[f],
      );
      if (activeSequence.length === 0) return;

      cascadePendingCount.current += childDims.length;
      setIsCascadeLoading(true);

      childDims.forEach((childDim) => {
        client
          .post("api/data_explorer_filtered_items/", {
            dimension: childDim,
            filters: currentFilters,
            filters_mode: currentFiltersMode,
            filters_sequence: activeSequence,
          })
          .then((response) => {
            if (response.data.success && response.data.items.length > 0) {
              cascadedItems.current[childDim] = response.data.items;
            }
          })
          .catch(() => {})
          .finally(() => {
            cascadePendingCount.current = Math.max(
              0,
              cascadePendingCount.current - 1,
            );
            if (cascadePendingCount.current === 0) {
              setIsCascadeLoading(false);
            }
          });
      });
    },
    [getChildDimsInGroup],
  );

  // ======================================================================
  // Dimension checkbox toggle
  // ======================================================================
  const handleDimensionToggle = (dim) => {
    if (isCascadeLoading) return;
    setEstimate(null);
    if (selectedDimensions.includes(dim)) {
      setSelectedDimensions((prev) => prev.filter((d) => d !== dim));
      setFilters((draft) => {
        draft[dim] = [];
      });
      setFiltersMode((draft) => {
        draft[dim] = "";
      });
      setFiltersIndexes((draft) => {
        draft[dim] = [];
      });
      setFiltersSequence((prev) => prev.filter((f) => f !== dim));
      // Clear ref range for this dimension
      if (refRangeFilters[dim]) {
        setRefRangeFilters((draft) => {
          delete draft[dim];
        });
      }
      const children = getChildDimsInGroup(dim);
      children.forEach((c) => delete cascadedItems.current[c]);
    } else {
      setSelectedDimensions((prev) => [...prev, dim]);
    }
  };

  // ======================================================================
  // Value checkbox toggle
  // ======================================================================
  const handleValueToggle = (val) => {
    // Prevent removing mandatory anchors
    if (
      mandatoryAnchors.current.includes(val) &&
      selectedValues.includes(val)
    ) {
      return;
    }
    setEstimate(null);
    if (selectedValues.includes(val)) {
      setSelectedValues((prev) => prev.filter((v) => v !== val));
    } else {
      setSelectedValues((prev) => [...prev, val]);
    }
  };

  const handleSelectAllValues = () => {
    setEstimate(null);
    const allValues = ["volume", ...pnlLines.current.map((l) => l.meta_name)];
    setSelectedValues(allValues);
  };

  const handleDeselectAllValues = () => {
    setEstimate(null);
    // Keep mandatory anchors selected
    setSelectedValues([...mandatoryAnchors.current]);
  };

  const handleSelectFirstLevel = () => {
    setEstimate(null);
    const firstLevelValues = [
      "volume",
      ...pnlLines.current.filter((l) => !l.parent).map((l) => l.meta_name),
    ];
    setSelectedValues(firstLevelValues);
  };

  // ======================================================================
  // Open filter modal — always INSTANT
  // ======================================================================
  const openFilterModal = (filterName) => {
    setFilterModalCurrentFilter(filterName);

    if (Object.keys(filtersGroups.current).includes(filterName)) {
      let choicesTranslated = [];
      for (const [key, value] of Object.entries(
        filtersGroups.current[filterName],
      )) {
        choicesTranslated.push([key, value[i18n.language]]);
      }
      setFilterModalGroupChoices(choicesTranslated);
    } else {
      setFilterModalGroupChoices([]);
    }

    setFilterModalFilterMode(filtersMode[filterName] || "");
    if (filtersMode[filterName] === "items") {
      setFilterModalGroupSelected("");
      setFilterModalItemsSelected(filtersIndexes[filterName] || []);
    } else if (filtersMode[filterName] === "group") {
      setFilterModalItemsSelected([]);
      setFilterModalGroupSelected(
        filters[filterName] && filters[filterName].length > 0
          ? filters[filterName][0]
          : "",
      );
    } else {
      setFilterModalGroupSelected("");
      setFilterModalItemsSelected([]);
    }

    // Populate ref range state if applicable
    const existingRange = refRangeFilters[filterName];
    if (filterName !== "period" && existingRange) {
      setFilterModalRangeMode(existingRange.mode);
      setFilterModalRangeMin(
        existingRange.min !== null ? String(existingRange.min) : "",
      );
      setFilterModalRangeMax(
        existingRange.max !== null ? String(existingRange.max) : "",
      );
      if (existingRange.mode && !filtersMode[filterName]) {
        setFilterModalFilterMode("range");
      }
    } else {
      setFilterModalRangeMode(null);
      setFilterModalRangeMin("");
      setFilterModalRangeMax("");
    }

    // Use pre-fetched cascaded items if available
    if (
      filterName !== "period" &&
      cascadedItems.current[filterName] &&
      cascadedItems.current[filterName].length > 0
    ) {
      const filteredChoices = cascadedItems.current[filterName];
      const itemsWithIndex = filteredChoices.map((display, index) => ({
        display,
        originalIndex: index,
      }));
      setFilterModalItemsChoices(itemsWithIndex);
      setFilterModalItemsSelected([]);
      setFilterModalFilterMode("");
    } else {
      const allItemsOriginal = filtersItems.current[filterName] || [];
      const displayChoices = getChoicesList(filterName, allItemsOriginal);
      const itemsWithIndex = displayChoices.map((display, index) => ({
        display,
        originalIndex: index,
      }));
      setFilterModalItemsChoices(itemsWithIndex);
    }

    setFilterModalOpen(true);
  };

  // ======================================================================
  // Filter modal apply
  // ======================================================================
  const handleFilterModalApply = () => {
    const filterName = filterModalCurrentFilter;
    const isCascaded =
      filterName !== "period" &&
      cascadedItems.current[filterName] &&
      cascadedItems.current[filterName].length > 0;

    let newFilters, newFiltersMode, newFiltersSequence;

    // Handle ref range mode
    if (filterModalFilterMode === "range" && filterName !== "period") {
      const minVal =
        filterModalRangeMin !== "" ? parseFloat(filterModalRangeMin) : null;
      const maxVal =
        filterModalRangeMax !== "" ? parseFloat(filterModalRangeMax) : null;
      setRefRangeFilters((draft) => {
        draft[filterName] = {
          mode: filterModalRangeMode,
          min: minVal,
          max: maxVal,
        };
      });
      // Clear any items for this dimension
      setFilters((draft) => {
        draft[filterName] = [];
      });
      setFiltersMode((draft) => {
        draft[filterName] = "";
      });
      setFiltersIndexes((draft) => {
        draft[filterName] = [];
      });
      setFiltersSequence((prev) => prev.filter((f) => f !== filterName));
      setEstimate(null);
      setFilterModalOpen(false);
      return;
    }

    if (
      filterModalFilterMode === "items" ||
      filterModalGroupChoices.length === 0
    ) {
      let selectedItemValues;
      if (isCascaded) {
        selectedItemValues = filterModalItemsSelected
          .map((index) => filterModalItemsChoices[index]?.display)
          .filter(Boolean);
      } else {
        selectedItemValues = filterModalItemsSelected.map(
          (index) => filtersItems.current[filterName][index],
        );
      }

      setFilters((draft) => {
        draft[filterName] = selectedItemValues;
      });
      setFiltersIndexes((draft) => {
        draft[filterName] = filterModalItemsSelected;
      });
      setFiltersMode((draft) => {
        draft[filterName] = "items";
      });

      newFilters = { ...filters, [filterName]: selectedItemValues };
      newFiltersMode = { ...filtersMode, [filterName]: "items" };
      newFiltersSequence = filtersSequence.includes(filterName)
        ? filtersSequence
        : [...filtersSequence, filterName];
      if (!filtersSequence.includes(filterName)) {
        setFiltersSequence(newFiltersSequence);
      }
    } else if (filterModalFilterMode === "group") {
      setFiltersIndexes((draft) => {
        draft[filterName] = [];
      });
      setFilters((draft) => {
        draft[filterName] = [filterModalGroupSelected];
      });
      setFiltersMode((draft) => {
        draft[filterName] = "group";
      });

      newFilters = { ...filters, [filterName]: [filterModalGroupSelected] };
      newFiltersMode = { ...filtersMode, [filterName]: "group" };
      newFiltersSequence = filtersSequence.includes(filterName)
        ? filtersSequence
        : [...filtersSequence, filterName];
      if (!filtersSequence.includes(filterName)) {
        setFiltersSequence(newFiltersSequence);
      }
    }

    // Pre-fetch cascading items for child dimensions
    if (filterName !== "period" && newFilters) {
      prefetchCascadedItems(
        filterName,
        newFilters,
        newFiltersMode,
        newFiltersSequence,
      );
    }

    setEstimate(null);
    setFilterModalOpen(false);
  };

  // ======================================================================
  // Filter modal clear & helpers
  // ======================================================================
  const handleFilterModalClear = () => {
    const filterName = filterModalCurrentFilter;
    if (filterName === "period") {
      notify("data_explorer.period_required", "warning");
      return;
    }
    setFilters((draft) => {
      draft[filterName] = [];
    });
    setFiltersMode((draft) => {
      draft[filterName] = "";
    });
    setFiltersIndexes((draft) => {
      draft[filterName] = [];
    });
    setFiltersSequence((prev) => prev.filter((f) => f !== filterName));
    // Clear ref range if it was for this dimension
    if (refRangeFilters[filterName]) {
      setRefRangeFilters((draft) => {
        delete draft[filterName];
      });
    }
    const children = getChildDimsInGroup(filterName);
    children.forEach((c) => delete cascadedItems.current[c]);
    setEstimate(null);
    setFilterModalOpen(false);
  };

  const handleFilterModalSelectNone = () => {
    setFilterModalItemsSelected([]);
    if (filterModalFilterMode === "items") setFilterModalFilterMode("");
  };

  const handleFilterModalGroupSelect = (event) => {
    setFilterModalFilterMode("group");
    setFilterModalGroupSelected(event.target.value);
    setFilterModalItemsSelected([]);
  };

  const removeFilter = (filter, item, index, all = false) => {
    if (filter === "period") {
      if (all || (filters[filter] && filters[filter].length <= 1)) {
        notify("data_explorer.period_required", "warning");
        return;
      }
    }
    let new_filter_items;
    if (filter === "period" && filtersMode[filter] === "items") {
      let month_item = filtersItems.current[filter][index];
      new_filter_items = all
        ? []
        : filters[filter].filter(
            (items) => JSON.stringify(items) !== JSON.stringify(month_item),
          );
    } else {
      new_filter_items = all
        ? []
        : filters[filter].filter((items) => items !== item);
    }
    setFilters((draft) => {
      draft[filter] = new_filter_items;
    });
    if (new_filter_items.length === 0) {
      setFiltersIndexes((draft) => {
        draft[filter] = [];
      });
      setFiltersMode((draft) => {
        draft[filter] = "";
      });
      setFiltersSequence((prev) => prev.filter((f) => f !== filter));
      const children = getChildDimsInGroup(filter);
      children.forEach((c) => delete cascadedItems.current[c]);
    } else {
      setFiltersIndexes((draft) => {
        draft[filter] = (filtersIndexes[filter] || []).filter(
          (i) => i !== index,
        );
      });
    }
    setEstimate(null);
  };

  // ======================================================================
  // API helpers
  // ======================================================================
  const buildApiParams = () => ({
    dimensions: selectedDimensions,
    values: selectedValues,
    filters: filters,
    filters_mode: filtersMode,
    filters_sequence: filtersSequence,
    time_granularity: timeGranularity,
    ref_range_filters: hasActiveRefRange ? buildRefRangeFiltersArray() : null,
  });

  const handleCheckFeasibility = () => {
    if (selectedDimensions.length === 0) {
      notify("data_explorer.select_at_least_one_dimension", "warning");
      return;
    }
    if (selectedValues.length === 0) {
      notify("data_explorer.select_at_least_one_value", "warning");
      return;
    }
    setIsEstimating(true);
    setEstimate(null);
    client
      .post("api/data_explorer_estimate/", buildApiParams())
      .then((response) => {
        setEstimate(response.data);
        setIsEstimating(false);
      })
      .catch(() => {
        notify("data_explorer.download_error", "error");
        setIsEstimating(false);
      });
  };

  const handleDownload = () => {
    if (isDownloading || !estimate || !estimate.can_download) return;
    setIsDownloading(true);
    notify("data_explorer.download_generating", "info");
    const params = { ...buildApiParams() };
    client
      .post("api/data_explorer_download/", params)
      .then(() => {
        fileDownloadClient
          .post("api/data_explorer_download/", params)
          .then((response) => {
            fileDownload(
              response.data,
              response.headers["content-disposition"].split("filename=")[1],
            );
            setIsDownloading(false);
          })
          .catch(() => {
            notify("data_explorer.download_error", "error");
            setIsDownloading(false);
          });
      })
      .catch(() => {
        notify("data_explorer.download_error", "error");
        setIsDownloading(false);
      });
  };

  // ======================================================================
  // Build grouped dimensions — fix duplicate "Others"
  // ======================================================================
  const getGroupedDimensions = () => {
    const groups = dimensionGroupsRef.current;
    const allDims = filtersList.current.filter((f) => f !== "period");

    if (!groups || groups.length === 0) {
      return [
        {
          key: "all",
          name: { en: "Dimensions", es: "Dimensiones", pt: "Dimensões" },
          dimensions: allDims,
        },
      ];
    }

    // Collect all dimensions already in any group
    const groupedDims = new Set();
    groups.forEach((g) => g.dimensions.forEach((d) => groupedDims.add(d)));

    // Only dims that are in filtersList AND not yet in any group
    const ungrouped = allDims.filter((f) => !groupedDims.has(f));

    // Filter group dimensions to only those in filtersList
    const allDimsSet = new Set(allDims);
    const result = groups
      .map((g) => ({
        ...g,
        dimensions: g.dimensions.filter((d) => allDimsSet.has(d)),
      }))
      .filter((g) => g.dimensions.length > 0);

    if (ungrouped.length > 0) {
      // Check if there's already an "others" group
      const hasOthers = result.some((g) => g.key === "others");
      if (hasOthers) {
        // Merge into existing others
        const othersGroup = result.find((g) => g.key === "others");
        ungrouped.forEach((d) => {
          if (!othersGroup.dimensions.includes(d)) {
            othersGroup.dimensions.push(d);
          }
        });
      } else {
        result.push({
          key: "others",
          name: { en: "Others", es: "Otros", pt: "Outros" },
          dimensions: ungrouped,
        });
      }
    }
    return result;
  };

  // ======================================================================
  // Active filters tag
  // ======================================================================
  const getRefRangeLabel = (dim) => {
    const rf = refRangeFilters[dim];
    if (!rf || !rf.mode) return null;
    const line = eligibleColorLines.current.find(
      (l) => l.meta_name === (selectedColorLine || serveMarginAnchor.current),
    );
    const lineName = line ? line.name : "";
    if (rf.mode === "gt") return `${lineName} ≥ ${rf.min}%`;
    if (rf.mode === "lt") return `${lineName} ≤ ${rf.max}%`;
    if (rf.mode === "between") return `${lineName} ${rf.min}%–${rf.max}%`;
    return null;
  };

  const getActiveFiltersForDimension = (dim) => {
    const hasItems = filters[dim] && filters[dim].length > 0;
    const hasRefRange = !!refRangeFilters[dim];
    if (!hasItems && !hasRefRange) return null;
    return (
      <div className={classes.filterTagWrapper}>
        {hasItems && (
          <FilterTag
            filtersList={filtersList.current}
            filtersNames={filtersNames.current}
            filtersGroups={filtersGroups.current}
            filter={dim}
            items={getFilterItems(filters[dim], dim, filtersMode[dim])}
            indexes={filtersIndexes[dim] || []}
            mode={filtersMode[dim] || ""}
            nameClickHandler={() => openFilterModal(dim)}
            removeItemHandler={removeFilter}
            color={"gray"}
          />
        )}
        {hasRefRange && (
          <FilterTag
            filtersList={["_ref_range_" + dim]}
            filtersNames={{
              ["_ref_range_" + dim]: {
                [i18n.language]: filtersNames.current[dim]
                  ? filtersNames.current[dim][i18n.language]
                  : dim,
              },
            }}
            filter={"_ref_range_" + dim}
            items={[getRefRangeLabel(dim) || ""]}
            indexes={[0]}
            mode="items"
            removeItemHandler={() =>
              setRefRangeFilters((draft) => {
                delete draft[dim];
              })
            }
            color="gray"
          />
        )}
      </div>
    );
  };

  // ======================================================================
  // RENDER
  // ======================================================================
  if (!metaLoaded) {
    return (
      <div className={classes.loadingWrapper}>
        <ThreeDots
          width="80px"
          height="80px"
          color={clientColor}
          ariaLabel="loading"
        />
      </div>
    );
  }

  const groupedDimensions = getGroupedDimensions();
  const dimensionsDisabled = isCascadeLoading;

  return (
    <div className={classes.container}>
      {/* LEFT PANEL */}
      <div className={classes.leftPanel}>
        {/* Period */}
        <Card className={classes.sectionCard}>
          <CardBody className={classes.sectionCardBody}>
            <div className={classes.sectionHeader}>
              <DateRange className={classes.sectionIcon} />
              <h5 className={classes.sectionTitle}>
                {t("data_explorer.period")}
              </h5>
            </div>
            <div className={classes.sectionContent}>
              <div className={classes.periodSection}>
                {filters["period"] && filters["period"].length > 0 ? (
                  <FilterTag
                    filtersList={filtersList.current}
                    filtersNames={filtersNames.current}
                    filtersGroups={filtersGroups.current}
                    filter={"period"}
                    items={getFilterItems(
                      filters["period"],
                      "period",
                      filtersMode["period"],
                    )}
                    indexes={filtersIndexes["period"] || []}
                    mode={filtersMode["period"] || "group"}
                    nameClickHandler={() => openFilterModal("period")}
                    removeItemHandler={removeFilter}
                    color={"gray"}
                  />
                ) : (
                  <Button
                    color="github"
                    simple
                    size="sm"
                    onClick={() => openFilterModal("period")}
                  >
                    {t("data_explorer.select_period")}
                  </Button>
                )}
              </div>
              <RadioGroup
                row
                value={timeGranularity}
                onChange={(e) => {
                  setTimeGranularity(e.target.value);
                  setEstimate(null);
                }}
                style={{ marginTop: "8px" }}
              >
                <FormControlLabel
                  value="monthly"
                  control={
                    <Radio
                      size="small"
                      style={{
                        color:
                          timeGranularity === "monthly"
                            ? clientColor
                            : undefined,
                        padding: "4px",
                      }}
                    />
                  }
                  label={
                    <span style={{ fontSize: "12px", color: grayColor[2] }}>
                      {t("data_explorer.granularity_monthly")}
                    </span>
                  }
                  style={{ marginRight: "20px" }}
                />
                <FormControlLabel
                  value="consolidated"
                  control={
                    <Radio
                      size="small"
                      style={{
                        color:
                          timeGranularity === "consolidated"
                            ? clientColor
                            : undefined,
                        padding: "4px",
                      }}
                    />
                  }
                  label={
                    <span style={{ fontSize: "12px", color: grayColor[2] }}>
                      {t("data_explorer.granularity_consolidated")}
                    </span>
                  }
                />
              </RadioGroup>
            </div>
          </CardBody>
        </Card>

        {/* Dimensions */}
        <Card className={cx(classes.sectionCard, classes.growCard)}>
          <CardBody className={classes.sectionCardBody}>
            <div className={classes.sectionHeader}>
              <ViewColumn className={classes.sectionIcon} />
              <h5 className={classes.sectionTitle}>
                {t("data_explorer.dimensions")}
              </h5>
              {isCascadeLoading && (
                <ThreeDots
                  color={clientColor}
                  height="12px"
                  width="30px"
                  ariaLabel="loading"
                />
              )}
            </div>
            <div
              className={classes.sectionContent}
              style={
                dimensionsDisabled
                  ? { opacity: 0.6, pointerEvents: "none" }
                  : {}
              }
            >
              <p className={classes.sectionDescription}>
                {t("data_explorer.dimensions_description")}
              </p>

              {groupedDimensions.map((group) => (
                <div key={group.key} className={classes.groupContainer}>
                  <div className={classes.groupHeader}>
                    {group.name[i18n.language] || group.name.en}
                  </div>
                  <div className={classes.dimensionsGrid}>
                    {group.dimensions.map((dim) => {
                      const isSelected = selectedDimensions.includes(dim);

                      return (
                        <div
                          key={dim}
                          className={cx(classes.dimensionRow, {
                            [classes.dimensionRowSelected]: isSelected,
                          })}
                        >
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={isSelected}
                                onChange={() => handleDimensionToggle(dim)}
                                className={classes.dimensionCheckbox}
                                icon={<RadioButtonUnchecked />}
                                checkedIcon={
                                  <CheckCircle style={{ color: clientColor }} />
                                }
                              />
                            }
                            label={
                              <span className={classes.dimensionLabel}>
                                {filtersNames.current[dim]
                                  ? filtersNames.current[dim][i18n.language]
                                  : dim}
                              </span>
                            }
                            style={{ marginRight: "4px", marginBottom: "0" }}
                          />
                          {isSelected && (
                            <Button
                              color="github"
                              simple
                              size="sm"
                              className={classes.filterButton}
                              onClick={() => openFilterModal(dim)}
                            >
                              {t("data_explorer.filter_button")}
                            </Button>
                          )}
                          {isSelected && getActiveFiltersForDimension(dim)}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}

              {selectedDimensions.length > 0 && (
                <p className={classes.selectedCount}>
                  {selectedDimensions.length}{" "}
                  {t("data_explorer.dimensions").toLowerCase()}
                </p>
              )}
            </div>
          </CardBody>
        </Card>
      </div>

      {/* RIGHT PANEL */}
      <div className={classes.rightPanel}>
        {/* Values */}
        <Card className={classes.sectionCard}>
          <CardBody className={classes.sectionCardBody}>
            <div className={classes.sectionHeader}>
              <Functions className={classes.sectionIcon} />
              <h5 className={classes.sectionTitle}>
                {t("data_explorer.values")}
              </h5>
            </div>
            <div className={classes.sectionContent}>
              <p
                className={classes.sectionDescription}
                style={{ margin: "0 0 6px 0" }}
              >
                {t("data_explorer.values_description")}
              </p>
              <div className={classes.valueActionLinks}>
                <span
                  className={classes.valueActionLink}
                  onClick={handleSelectFirstLevel}
                >
                  {t("data_explorer.select_first_level")}
                </span>
                <span
                  className={classes.valueActionLink}
                  onClick={
                    selectedValues.length === pnlLines.current.length + 1
                      ? handleDeselectAllValues
                      : handleSelectAllValues
                  }
                >
                  {selectedValues.length === pnlLines.current.length + 1
                    ? t("data_explorer.deselect_all")
                    : t("data_explorer.select_all")}
                </span>
              </div>
              <div className={classes.valuesGrid}>
                {/* Volume row */}
                <div className={cx(classes.valueRow, classes.valueRowParent)}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedValues.includes("volume")}
                        onChange={() => handleValueToggle("volume")}
                        className={classes.valueCheckbox}
                        icon={<RadioButtonUnchecked />}
                        checkedIcon={
                          <CheckCircle style={{ color: clientColor }} />
                        }
                      />
                    }
                    label={
                      <span
                        className={cx(
                          classes.valueLabel,
                          classes.valueLabelParent,
                        )}
                      >
                        {"Volume" +
                          (volumeUnit.current
                            ? " (" + volumeUnit.current + ")"
                            : "")}
                      </span>
                    }
                    style={{ marginBottom: "0" }}
                  />
                </div>
                {/* P&L lines */}
                {pnlLines.current.map((line, idx) => {
                  const isChild = !!line.parent;
                  const isMandatory = mandatoryAnchors.current.includes(
                    line.meta_name,
                  );
                  const hasChildren = line.is_parent;
                  const isExpanded = expandedParents.includes(line.meta_name);

                  // Hide child rows when parent is collapsed
                  if (isChild && !expandedParents.includes(line.parent)) {
                    return null;
                  }

                  const level = line.level || 1;

                  return (
                    <div
                      key={line.meta_name}
                      className={cx(classes.valueRow, {
                        [classes.valueRowParent]: !isChild,
                        [classes.valueRowMandatory]: isMandatory,
                        [classes.valueRowEven]: isChild && idx % 2 === 0,
                      })}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectedValues.includes(line.meta_name)}
                            onChange={() => handleValueToggle(line.meta_name)}
                            disabled={isMandatory}
                            className={classes.valueCheckbox}
                            icon={<RadioButtonUnchecked />}
                            checkedIcon={
                              <CheckCircle
                                style={{
                                  color: isMandatory
                                    ? grayColor[3]
                                    : clientColor,
                                }}
                              />
                            }
                          />
                        }
                        label={
                          <span
                            className={cx(classes.valueLabel, {
                              [classes.valueLabelParent]: !isChild,
                              [classes.valueLabelChild]: isChild,
                              [classes.valueLabelLevel2]:
                                isChild && level === 2,
                              [classes.valueLabelLevel3]: isChild && level >= 3,
                            })}
                          >
                            {line.name}
                          </span>
                        }
                        style={{ marginBottom: "0" }}
                      />
                      {hasChildren && (
                        <span
                          className={classes.collapseToggle}
                          onClick={() =>
                            setExpandedParents((prev) =>
                              prev.includes(line.meta_name)
                                ? prev.filter((p) => p !== line.meta_name)
                                : [...prev, line.meta_name],
                            )
                          }
                        >
                          {isExpanded ? (
                            <ExpandLess style={{ fontSize: "18px" }} />
                          ) : (
                            <ExpandMore style={{ fontSize: "18px" }} />
                          )}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
              {selectedValues.length > 0 && (
                <p className={classes.selectedCount}>
                  {selectedValues.length}{" "}
                  {t("data_explorer.values").toLowerCase()}
                </p>
              )}
            </div>
          </CardBody>
        </Card>

        {/* Download */}
        <Card className={cx(classes.sectionCard, classes.growCard)}>
          <CardBody className={classes.sectionCardBody}>
            <div className={classes.sectionHeader}>
              <CloudDownload className={classes.sectionIcon} />
              <h5 className={classes.sectionTitle}>
                {t("data_explorer.download")}
              </h5>
            </div>
            <div className={classes.sectionContent}>
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <Button
                  color="github"
                  size="sm"
                  className={classes.feasibilityBtn}
                  onClick={handleCheckFeasibility}
                  disabled={
                    isEstimating ||
                    isDownloading ||
                    selectedDimensions.length === 0 ||
                    selectedValues.length === 0
                  }
                >
                  {t("data_explorer.check_feasibility")}
                  {isEstimating && (
                    <ThreeDots
                      color="#fff"
                      height="14px"
                      width="30px"
                      ariaLabel="loading"
                      wrapperStyle={{
                        marginLeft: "8px",
                        display: "inline-flex",
                      }}
                    />
                  )}
                </Button>
                <Tooltip title={t("data_explorer.check_feasibility_tooltip")}>
                  <HelpOutline
                    style={{
                      fontSize: "18px",
                      color: grayColor[6],
                      cursor: "help",
                    }}
                  />
                </Tooltip>
              </div>

              {estimate && (
                <div className={classes.estimateBox}>
                  <p className={classes.estimateResult}>
                    {estimate.can_download ? (
                      <span className={classes.estimateSuccess}>
                        {"✓ "}
                        {t("data_explorer.estimate_result", {
                          rows: estimate.estimated_rows.toLocaleString(),
                          cols: estimate.estimated_columns,
                          maxRows:
                            estimate.max_rows_for_config.toLocaleString(),
                        })}
                      </span>
                    ) : (
                      <>
                        <span className={classes.estimateError}>
                          {"✕ "}
                          {t("data_explorer.estimate_exceeded", {
                            rows: estimate.estimated_rows.toLocaleString(),
                            cols: estimate.estimated_columns,
                            maxRows:
                              estimate.max_rows_for_config.toLocaleString(),
                          })}
                        </span>
                        <br />
                        <span className={classes.estimateSuggestion}>
                          {t("data_explorer.estimate_suggestion")}
                        </span>
                      </>
                    )}
                  </p>
                </div>
              )}

              <div className={classes.downloadButtons}>
                <Button
                  color="github"
                  simple
                  size="sm"
                  className={classes.downloadBtn}
                  disabled={
                    !estimate ||
                    !estimate.can_download ||
                    isDownloading ||
                    isEstimating
                  }
                  onClick={() => handleDownload()}
                >
                  <GetApp />
                  {isDownloading
                    ? t("data_explorer.downloading")
                    : t("data_explorer.download_excel")}
                </Button>
              </div>
            </div>
          </CardBody>
        </Card>
      </div>

      {/* Filter Dialog */}
      <FilterDialog
        open={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        headerTitle={
          filtersNames.current[filterModalCurrentFilter]
            ? filtersNames.current[filterModalCurrentFilter][i18n.language]
            : filterModalCurrentFilter
        }
        headerScope={t("data_explorer.filter_scope")}
        selectedItems={filterModalItemsSelected}
        onItemsSelect={(indexes) => {
          setFilterModalItemsSelected(indexes);
          setFilterModalFilterMode(indexes.length > 0 ? "items" : "");
          if (indexes.length > 0) {
            setFilterModalRangeMode(null);
            setFilterModalRangeMin("");
            setFilterModalRangeMax("");
          }
        }}
        onSelectNone={handleFilterModalSelectNone}
        onClearFilter={
          filterModalCurrentFilter !== "period"
            ? handleFilterModalClear
            : undefined
        }
        onApply={handleFilterModalApply}
        groupChoices={filterModalGroupChoices}
        groupSelected={filterModalGroupSelected}
        onGroupSelect={handleFilterModalGroupSelect}
        filterMode={filterModalFilterMode}
        clientColor={clientColor}
        allItems={filterModalItemsChoices}
        refRangeSection={
          filterModalCurrentFilter &&
          filterModalCurrentFilter !== "period" &&
          selectedDimensions.includes(filterModalCurrentFilter)
            ? {
                visible: true,
                refLineName: (() => {
                  const line = eligibleColorLines.current.find(
                    (l) => l.meta_name === selectedColorLine,
                  );
                  return line ? line.name : "";
                })(),
                mode: filterModalRangeMode,
                min: filterModalRangeMin,
                max: filterModalRangeMax,
                onModeChange: (m) => {
                  setFilterModalRangeMode(m);
                  setFilterModalFilterMode("range");
                  setFilterModalItemsSelected([]);
                  setFilterModalGroupSelected("");
                },
                onMinChange: (val) => setFilterModalRangeMin(val),
                onMaxChange: (val) => setFilterModalRangeMax(val),
              }
            : undefined
        }
      />
    </div>
  );
}
